import React, { useState, useEffect } from 'react';
import { validateInviteCode, useInviteCode } from '../services/inviteCodeService';
import { addToWaitlist } from '../services/waitlistService';
import { getDeviceId } from '../utils/deviceFingerprint';
import { AuthService } from '../services/authService';
import { hasUserSiteAccessRecord } from '../services/userSiteAccessService';
import { getCleanErrorMessage } from '../utils/errorParser.js';
import './InviteGate.css';

const InviteGate = ({ onAccessGranted }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [urlCodeProcessing, setUrlCodeProcessing] = useState(false);
  /** Firestore rate limits are keyed by auth uid; anonymous sign-in must run before invite validation. */
  const [firebaseReady, setFirebaseReady] = useState(false);

  // Restore access from Firestore only (localStorage is not trusted for gate decisions).
  useEffect(() => {
    if (!firebaseReady) return;
    let cancelled = false;
    (async () => {
      try {
        const ok = await hasUserSiteAccessRecord();
        if (cancelled) return;
        if (ok) {
          setError('');
          try {
            localStorage.setItem('stabletrust:hasAccess', 'true');
          } catch {}
          onAccessGranted();
          return;
        }
      } catch (e) {
        console.error('[InviteGate] Failed to check Firestore access:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseReady, onAccessGranted]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!AuthService.isAuthenticated()) {
          await AuthService.signInAnonymously();
        }
      } catch (e) {
        console.error('[InviteGate] Firebase anonymous sign-in failed:', e);
      } finally {
        if (!cancelled) setFirebaseReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check for invite code in URL parameters
  useEffect(() => {
    if (!firebaseReady) return;

    const processUrlInviteCode = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlInviteCode = urlParams.get('invite');
        
        if (!urlInviteCode) return;
        
        // Clean the URL by removing the invite parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('invite');
        window.history.replaceState({}, '', newUrl.toString());
        
        setUrlCodeProcessing(true);
        setInviteCode(urlInviteCode.toUpperCase());
        
        // Get device ID for rate limiting and tracking
        const deviceId = getDeviceId();
        
        // Validate the code
        const validation = await validateInviteCode(urlInviteCode.trim(), deviceId);
        
        if (validation.rateLimited) {
          setError(`Too many attempts. Try again in ${validation.resetInMinutes} minutes.`);
          setUrlCodeProcessing(false);
          return;
        }
        
        if (!validation.valid) {
          setError('Invalid invite code from link');
          setUrlCodeProcessing(false);
          return;
        }

        if (validation.used && !validation.isAdmin) {
          setError('This invite code has already been used');
          setUrlCodeProcessing(false);
          return;
        }

        // Code is valid, mark it as used
        const result = await useInviteCode(urlInviteCode.trim(), deviceId);

        if (result.success) {
          // Grant access
          localStorage.setItem('stabletrust:hasAccess', 'true');
          localStorage.setItem('stabletrust:accessCode', urlInviteCode.trim().toUpperCase());
          onAccessGranted();
        } else {
          setError(getCleanErrorMessage(result.error) || 'Failed to process invite code');
        }
      } catch (err) {
        console.error('Error processing URL invite code:', err);
        setError('Could not process the invite link.');
      } finally {
        setUrlCodeProcessing(false);
      }
    };
    
    processUrlInviteCode();
  }, [onAccessGranted, firebaseReady]);

  const handleCodeChange = (e) => {
    setInviteCode(e.target.value.toUpperCase());
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      setError('Enter an invite code');
      setIsValidating(false);
      setIsSubmitting(false);
      return;
    }

    // Check if user entered an email instead of a code
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(inviteCode.trim())) {
      setError('This looks like an email address. Click "Join Waitlist" to submit your email, or enter an invite code.');
      setIsValidating(false);
      setIsSubmitting(false);
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      if (!firebaseReady) {
        setError('Wait a moment and try again.');
        setIsValidating(false);
        return;
      }

      // Get device ID for rate limiting and tracking
      const deviceId = getDeviceId();
      
      // Validate the code (with rate limiting)
      const validation = await validateInviteCode(inviteCode.trim(), deviceId);
      
      if (validation.rateLimited) {
        setError(`Too many attempts. Try again in ${validation.resetInMinutes} minutes.`);
        setIsValidating(false);
        return;
      }
      
      if (!validation.valid) {
        const remaining = validation.remainingAttempts !== undefined 
          ? ` (${validation.remainingAttempts} attempts remaining)`
          : '';
        setError(`Invalid invite code${remaining}`);
        setIsValidating(false);
        return;
      }

      // Admin codes are never "used" - they can be reused
      if (validation.used && !validation.isAdmin) {
        setError('This invite code has already been used');
        setIsValidating(false);
        return;
      }

      // Code is valid and unused, mark it as used
      setIsSubmitting(true);
      const result = await useInviteCode(inviteCode.trim(), deviceId);

      if (result.success) {
        // Grant access
        localStorage.setItem('stabletrust:hasAccess', 'true');
        localStorage.setItem('stabletrust:accessCode', inviteCode.trim());
        onAccessGranted();
      } else {
        setError(getCleanErrorMessage(result.error) || 'Failed to process invite code');
        setIsSubmitting(false);
        setIsValidating(false);
      }
    } catch (err) {
      console.error('Error processing invite code:', err);
      setError('Something went wrong. Try again.');
      setIsValidating(false);
      setIsSubmitting(false);
    }
  };

  const handleJoinWaitlist = () => {
    setShowWaitlist(true);
    setWaitlistEmail('');
    setError('');
    setWaitlistSuccess(false);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    if (!waitlistEmail.trim()) {
      setError('Enter your email address');
      return;
    }

    setIsSubmittingWaitlist(true);
    setError('');

    try {
      const result = await addToWaitlist(waitlistEmail.trim());
      
      if (result.success) {
        setWaitlistSuccess(true);
        setWaitlistEmail('');
        // Hide the form after 3 seconds
        setTimeout(() => {
          setShowWaitlist(false);
          setWaitlistSuccess(false);
        }, 3000);
      } else {
        setError(getCleanErrorMessage(result.error) || 'Failed to join waitlist');
      }
    } catch (err) {
      console.error('Error joining waitlist:', err);
      setError('Something went wrong. Try again.');
    } finally {
      setIsSubmittingWaitlist(false);
    }
  };

  const handleCancelWaitlist = () => {
    setShowWaitlist(false);
    setWaitlistEmail('');
    setError('');
    setWaitlistSuccess(false);
  };

  return (
    <div className="invite-gate-container">
      {/* Header - Mobile Only */}
      <header className="invite-gate-header">
        <div className="invite-gate-header-content">
          <div className="invite-gate-logo">
            <img src="/blacklogo.png" alt="Stabletrust Pay" className="invite-gate-logo-img" />
          </div>
          <button className="invite-gate-menu-button" aria-label="Menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </header>

      <div className="invite-gate-content">
        {/* Video/Animation Section - Desktop Only */}
        <div className="invite-gate-video-section">
          <div className="invite-gate-video-wrapper">
            <video 
              className="invite-gate-video" 
              autoPlay 
              loop 
              muted 
              playsInline
              preload="auto"
            >
              <source src="/grey.mp4" type="video/mp4" />
              <source src="/grey.mov" type="video/quicktime" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Form Section */}
        <div className="invite-gate-form-section">
          <div className="invite-gate-form-container">
            <h1 className="invite-gate-title">Stabletrust Pay is invite-only</h1>
            <p className="invite-gate-description">
              Enter your invite code to continue or join the waitlist. We'll reach out when you're eligible.
            </p>

            {!showWaitlist ? (
              <form onSubmit={handleSubmit} className="invite-gate-form">
                <div className="invite-gate-input-group">
                  <input
                    type="text"
                    className="invite-gate-input"
                    placeholder="Enter invite code"
                    value={inviteCode}
                    onChange={handleCodeChange}
                    disabled={isValidating || isSubmitting || urlCodeProcessing || !firebaseReady}
                    autoComplete="off"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="invite-gate-submit-button"
                    disabled={isValidating || isSubmitting || urlCodeProcessing || !firebaseReady}
                  >
                    {!firebaseReady
                      ? 'Connecting...'
                      : isValidating || isSubmitting || urlCodeProcessing
                        ? 'Processing...'
                        : 'Next'}
                  </button>
                </div>

                {error && (
                  <div className="invite-gate-error">
                    {error}
                  </div>
                )}

                <div className="invite-gate-divider">
                  <span>or</span>
                </div>

                <button
                  type="button"
                  className="invite-gate-waitlist-button"
                  onClick={handleJoinWaitlist}
                  disabled={!firebaseReady}
                >
                  Join Waitlist
                </button>
              </form>
            ) : (
              <div className="invite-gate-waitlist-form">
                {waitlistSuccess ? (
                  <div className="invite-gate-waitlist-success">
                    <p>✓ Thank you! We'll reach out when you're eligible.</p>
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} className="invite-gate-waitlist-email-form">
                    <input
                      type="email"
                      className="invite-gate-input"
                      placeholder="Enter your email address"
                      value={waitlistEmail}
                      onChange={(e) => {
                        setWaitlistEmail(e.target.value);
                        setError('');
                      }}
                      disabled={isSubmittingWaitlist || !firebaseReady}
                      autoComplete="email"
                      autoFocus
                      required
                    />
                    {error && (
                      <div className="invite-gate-error">
                        {error}
                      </div>
                    )}
                    <div className="invite-gate-waitlist-buttons">
                      <button
                        type="submit"
                        className="invite-gate-waitlist-submit"
                        disabled={isSubmittingWaitlist || !waitlistEmail.trim() || !firebaseReady}
                      >
                        {isSubmittingWaitlist ? 'Submitting...' : 'Submit'}
                      </button>
                      <button
                        type="button"
                        className="invite-gate-waitlist-cancel"
                        onClick={handleCancelWaitlist}
                        disabled={isSubmittingWaitlist}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteGate;
